import {removeByIndex, replaceByIndex} from 'common/query-builder/cohorts-query-builder/util';
import {joinExpressions} from 'common/query-builder/cohorts-query-builder/util/expression-tree';

import {LogicalOperator} from '../enums/expression-enums';
import {OperatorNode} from '../expression/operator-node';
import {expressionToLiteralRoot} from '../expression/utils';
import {ClauseOperators, StagedPropertyFilterClause} from '../types/builder-state-types';
import {ExpressionTree} from '../types/expression-types';
import {Serializable} from '../types/serialized-cohort-types';
import {SerializedLiteral} from '../types/serialized-staged-cohort-types';
import type {Clause} from './clause';
import type {ValidationArgs} from './staged-cohort';

interface LiteralOptions {
  clauses: Clause[];
  labelPrefix: string;
  clauseOperators?: ClauseOperators | null;
}

export abstract class Literal implements Serializable, ExpressionTree {
  public readonly clauses: Clause[];
  public readonly labelPrefix: string;
  public readonly clauseOperators: ClauseOperators = null;

  constructor(options: LiteralOptions) {
    Object.assign(this, options);
  }

  public abstract addDefaultClause(): Literal;
  public abstract setClauseOperators(clauseOperators: ClauseOperators): Literal;
  public abstract setClauseNegated(negated: boolean, clauseIdx: number): Literal;
  public abstract toTrackingData(): Obj;
  public abstract sanitizeForDataGroup({dataGroupId, customEvents, cohorts}: ValidationArgs): Literal;
  public abstract serialize(): SerializedLiteral;

  public getClauseOperators(): ClauseOperators {
    return this.clauseOperators;
  }

  get isFunnel(): boolean {
    return false;
  }

  public getExpressionTree(): OperatorNode {
    const clauseExpressions = this.clauses.map((clause) => clause.getExpressionTree());
    let expressionTree = clauseExpressions[0];
    if (this.clauseOperators) {
      const initialClauseOperator = this.clauseOperators[0];
      const joinOperator = this.clauseOperators[1];
      expressionTree = new OperatorNode(
        initialClauseOperator,
        expressionTree,
        joinExpressions(clauseExpressions.slice(1), joinOperator),
      );
    }

    return expressionToLiteralRoot(expressionTree);
  }

  public removeClauseByIndex(clauseIdx: number): Literal {
    let newLiteralOptions: Partial<LiteralOptions> = {clauses: removeByIndex(this.clauses, clauseIdx)};
    if (newLiteralOptions.clauses.length === 1) {
      newLiteralOptions = {...newLiteralOptions, ...{clauseOperators: null}};
    }

    return this.newLiteral(newLiteralOptions);
  }

  public isInvalidForDataGroup({dataGroupId, customEvents, cohorts}): boolean {
    return this.clauses.some((clause) => clause.isInvalidForDataGroup({dataGroupId, customEvents, cohorts}));
  }

  public isValid(): boolean {
    return this.clauses.every((c) => c.isValid());
  }

  public getLabel(): string {
    if (this.clauses.length === 1) {
      return this.labelPrefix;
    }

    let label = this.clauses.reduce((partialLabel, clause, idx) => {
      const operator = this.getOperatorLabelAtIndex(idx);
      const clauseLabel = clause.isNegated() ? `not ${this.getClauseLabel(idx)}` : this.getClauseLabel(idx);
      return idx === 0 ? clauseLabel : `${partialLabel} ${operator} ${clauseLabel}`;
    }, ``);

    // parenthesize second part of the label clause if need be
    if (this.clauses.length >= 3 && this.getOperatorLabelAtIndex(1) !== this.getOperatorLabelAtIndex(2)) {
      const operatorLabel = this.getOperatorLabelAtIndex(1);
      const [front, back] = label.split(` ${operatorLabel} `);
      label = `${front} ${operatorLabel} (${back})`;
    }

    return label;
  }

  public removePropertyFilter(clauseIdx: number, propertyFilterIdx: number): Literal {
    return this.replaceClause(this.clauses[clauseIdx].removePropertyFilter(propertyFilterIdx), clauseIdx);
  }

  public commitStagedFilterClause(clauseIdx: number, propertyFilterIdx: number): Literal {
    return this.replaceClause(this.clauses[clauseIdx].commitStagedFilterClause(propertyFilterIdx), clauseIdx);
  }

  public setStagedFilterClause(
    options: Partial<StagedPropertyFilterClause>,
    clauseIdx: number,
    propertyFilterIdx: number,
  ): Literal {
    return this.replaceClause(this.clauses[clauseIdx].setStagedFilterClause(options, propertyFilterIdx), clauseIdx);
  }

  public togglePropertyFilterOperator(clauseIdx: number): Literal {
    const newClause = this.clauses[clauseIdx].togglePropertyFilterOperator();
    return this.replaceClause(newClause, clauseIdx);
  }

  public getClauseLabel(idx: number): string {
    return `${this.labelPrefix}${idx + 1}`;
  }

  public setLabelPrefix(labelPrefix: string): Literal {
    return this.newLiteral({labelPrefix});
  }

  protected abstract newLiteral(options: Partial<LiteralOptions>): Literal;
  protected abstract getOperatorLabelAtIndex(idx: number): string;

  protected getDefaultClauseOperators(): ClauseOperators {
    return [LogicalOperator.And, LogicalOperator.And];
  }

  protected addClause<T extends Literal>(clause: Clause): T {
    let newLiteralOptions: Partial<LiteralOptions> = {clauses: [...this.clauses, clause]};
    if (this.clauses.length === 1) {
      newLiteralOptions = {...newLiteralOptions, ...{clauseOperators: this.getDefaultClauseOperators()}};
    }

    return this.newLiteral(newLiteralOptions) as T;
  }

  protected replaceClause<T extends Literal>(newClause: Clause, idx: number): T {
    const clauses = replaceByIndex(this.clauses, idx, newClause);
    return this.newLiteral({clauses}) as T;
  }
}
